import {getActivePinia} from 'pinia'
import {useUserStore} from '@/store/user'
import {useConfigurationDataStore} from '@/store/configuration-data'
import {CONFIGURATION_KEYS} from '~/assets/js/constants'
import * as Sentry from '@sentry/browser'

/**
 * Intended auth provider from the `auth_provider` cookie. Signals INTENT only
 * (which flow a logged-out user should start), so use it for the login button.
 */
export function isKeycloakAuth(): boolean {
  if (!process.client) {
    return false
  }

  const cookies = document.cookie.split(';')
  return cookies.some(c => c.trim() === 'auth_provider=keycloak')
}

// Persisted user store blob (key defaults to the store id). Fallback for reading the
// session before Pinia is ready (the early auth plugin runs before Pinia).
const PERSISTED_USER_KEY = 'UserModule'

// Diagnostic: remember which (userUuid + caller) combos we already logged, so the
// per-request/per-navigation calls don't spam Sentry with duplicates.
const loggedKeycloakSessions = new Set<string>()

// Internal / known email domains we don't need diagnostics for.
const SKIPPED_LOG_DOMAINS = ['fst.com', 'fnst.com', 'techniparts.nl']

// Call sites that can trigger a login/token exchange (e.g. the refresh process). These are
// still logged even without a userUuid, because that's exactly the interesting "logged out
// but going Keycloak" case. Everything else is skipped when there is no userUuid.
const LOGIN_TRIGGERING_CALLERS = ['axios_auth.getToken']

// Diagnostic logging only. Wrapped so a failure in here can never break isKeycloakSession().
function logKeycloakSession(source: string, caller: string): void {
  try {
    let userUuid
    let customerUuid
    let enableKeycloakAuth
    if (getActivePinia()) {
      const userStore = useUserStore()
      const email = (userStore.email ?? '').toLowerCase()
      if (SKIPPED_LOG_DOMAINS.some(domain => email.endsWith(`@${domain}`))) {
        return
      }
      userUuid = userStore.userUuid
      customerUuid = userStore.loadedCustomerUuid
      enableKeycloakAuth = (useConfigurationDataStore().configurations as any[])?.find(c => c.key === CONFIGURATION_KEYS.ENABLE_KEYCLOAK_AUTH)?.value
    }

    // Skip anonymous calls unless this call site can trigger a login (e.g. the refresh process).
    if (!userUuid && !LOGIN_TRIGGERING_CALLERS.includes(caller)) {
      return
    }

    const dedupeKey = `${userUuid}|${caller}`
    if (!loggedKeycloakSessions.has(dedupeKey)) {
      loggedKeycloakSessions.add(dedupeKey)
      Sentry.captureMessage(`isKeycloakSession=true (source: ${source}, cookieSet: ${isKeycloakAuth()}, EnableKeycloakAuth: ${enableKeycloakAuth}, userUuid: ${userUuid}, customerUuid: ${customerUuid}, caller: ${caller})`, {level: 'info'})
    }
  } catch {
    // diagnostic logging must never affect auth flow
  }
}

function getPersistedUserSession(): {accessToken?: string; loggedInUsingKeycloakFlow?: boolean} {
  if (!process.client) {
    return {}
  }

  try {
    const raw = window.localStorage.getItem(PERSISTED_USER_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function getUserSession(): {accessToken?: string; loggedInUsingKeycloakFlow?: boolean} {
  // Prefer the live store; fall back to the persisted blob before Pinia is active.
  if (getActivePinia()) {
    const userStore = useUserStore()
    return {accessToken: userStore.accessToken, loggedInUsingKeycloakFlow: userStore.loggedInUsingKeycloakFlow}
  }

  return getPersistedUserSession()
}

/**
 * Provider of the ACTIVE session: the flow it logged in with (the flag) while a
 * session exists, else the `auth_provider` intent cookie for the next login.
 * `caller` is a diagnostic label identifying the call site.
 */
export function isKeycloakSession(caller = 'unknown'): boolean {
  if (!process.client) {
    return false
  }

  const {accessToken, loggedInUsingKeycloakFlow} = getUserSession()
  const result = accessToken ? Boolean(loggedInUsingKeycloakFlow) : isKeycloakAuth()

  if (result) {
    logKeycloakSession(accessToken ? 'flag' : 'cookie', caller)
  }

  return result
}
